import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  clusterApi,
  Cluster,
  ClusterStatus,
  CreateClusterRequest,
  UpdateClusterRequest,
  TokenRotateRequest,
  Agent,
  ClusterHealth,
  AgentSettingsUpdateRequest,
} from '../api/clusterApi'
import { scopedKey } from '@/lib/scopedQueryKey'

// Query keys
export const clusterKeys = {
  // Tenant+user scoped (issue #301). A getter so the prefix is read at call
  // time; every derived key (and prefix invalidation) inherits it.
  get all() {
    return scopedKey('clusters')
  },
  lists: () => [...clusterKeys.all, 'list'] as const,
  list: (filters: { status?: ClusterStatus; limit?: number; offset?: number; includeDeleted?: boolean; includeArchived?: boolean }) =>
    [...clusterKeys.lists(), filters] as const,
  details: () => [...clusterKeys.all, 'detail'] as const,
  detail: (id: string) => [...clusterKeys.details(), id] as const,
  default: () => [...clusterKeys.all, 'default'] as const,
  assigned: () => [...clusterKeys.all, 'assigned'] as const,
  assignments: () => [...clusterKeys.all, 'assignments'] as const,
  agents: (clusterId: string) => [...clusterKeys.all, 'agents', clusterId] as const,
  events: (clusterId: string) => [...clusterKeys.all, 'events', clusterId] as const,
  health: (clusterId: string) => [...clusterKeys.all, 'health', clusterId] as const,
  workloads: (clusterId: string) => [...clusterKeys.all, 'workloads', clusterId] as const,
  connectivity: (clusterId: string) => [...clusterKeys.all, 'connectivity', clusterId] as const,
  agentSettings: (clusterId: string) => [...clusterKeys.all, 'agent-settings', clusterId] as const,
  agentUpgradeSummary: () => [...clusterKeys.all, 'agent-upgrade-summary'] as const,
  /** One-click agent upgrade view + latest request of ONE cluster (ADR-303). */
  agentOneClick: (clusterId: string) => [...clusterKeys.all, 'agent-one-click', clusterId] as const,
}

/** Tenant-wide below-minimum agent summary for the console banner (owners/admins). */
export function useAgentUpgradeSummary(enabled: boolean) {
  return useQuery({
    queryKey: clusterKeys.agentUpgradeSummary(),
    queryFn: async () => (await clusterApi.getAgentUpgradeSummary()).data,
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Hook to fetch all clusters
 */
export function useClusters(options?: {
  status?: ClusterStatus
  limit?: number
  offset?: number
  enabled?: boolean
  includeDeleted?: boolean
  /**
   * Archived clusters are excluded server-side by default (migration
   * 000980's `archived_at IS NULL` predicate) -- there is no single "return
   * everything" list mode. When true, this hook issues a SECOND request for
   * the archived-only set (`?archived=true`) alongside the normal default
   * request and merges the two client-side, so callers (ClusterList) get
   * one array they can filter with the shared search/status/archived
   * predicate instead of switching between two separate query results.
   */
  includeArchived?: boolean
}) {
  const { status, limit = 20, offset = 0, enabled = true, includeDeleted = false, includeArchived = false } = options || {}

  return useQuery({
    queryKey: clusterKeys.list({ status, limit, offset, includeDeleted, includeArchived }),
    queryFn: async ({ signal }) => {
      const response = await clusterApi.getAll(limit, offset, status, includeDeleted, false, signal)
      // An empty page comes back as `"data": null` (see below); callers
      // destructure with `= []`, which does not apply to null.
      if (!includeArchived) return response.data ?? []
      const archivedResponse = await clusterApi.getAll(limit, offset, status, includeDeleted, true, signal)
      // An empty page comes back as `"data": null`, not [] (the Go list is a nil
      // slice), and most tenants have no archived clusters — both halves must
      // tolerate it, or the whole list fails to load.
      const merged = [...(response.data ?? [])]
      const seen = new Set(merged.map((c) => c.id))
      for (const cluster of archivedResponse.data ?? []) {
        if (!seen.has(cluster.id)) merged.push(cluster)
      }
      return merged
    },
    enabled,
    staleTime: 30000, // 30 seconds
    // Poll faster when any cluster is provisioning or has a transient status
    refetchInterval: (query) => {
      const data = query.state.data as Cluster[] | undefined
      const hasTransient = data?.some(
        (c) => c.status === 'provisioning' || c.status === 'requested' || c.status === 'pending_agent' || c.status === 'agent_connecting' || c.status === 'deleting'
      )
      return hasTransient ? 5000 : false // 5s during provisioning, no auto-poll otherwise
    },
  })
}

/**
 * Hook to fetch a single cluster
 */
export function useCluster(id: string, options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.detail(id),
    queryFn: async ({ signal }) => {
      const response = await clusterApi.getById(id, signal)
      return response.data
    },
    enabled: enabled && !!id,
    staleTime: 30000,
    // Poll faster when cluster is in a transient state
    refetchInterval: (query) => {
      const data = query.state.data as Cluster | undefined
      const isTransient = data?.status === 'provisioning' || data?.status === 'requested' || data?.status === 'pending_agent' || data?.status === 'agent_connecting' || data?.status === 'deleting'
      return isTransient ? 5000 : false
    },
  })
}

/**
 * Hook to fetch the default cluster
 */
export function useDefaultCluster(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.default(),
    queryFn: async () => {
      const response = await clusterApi.getDefault()
      return response.data
    },
    enabled,
    staleTime: 60000, // 1 minute
  })
}

/**
 * Hook to fetch the tenant's cluster ROUTING assignments (workflow-specific
 * overrides plus the tenant default, `is_default: true`). This is the data
 * agentic chat/run dispatch actually resolves against
 * (ClusterResolverImpl.ResolveClusterForTenant, internal/domain/ai/
 * cluster_resolver_impl.go) — distinct from, and NOT interchangeable with,
 * useDefaultCluster below (that hook wraps the unrelated "first active BYOK
 * cluster" endpoint).
 */
export function useClusterAssignments(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.assignments(),
    queryFn: async () => {
      const response = await clusterApi.listAssignments()
      return response.data
    },
    enabled,
    staleTime: 30000,
  })
}

/**
 * Hook to fetch assigned managed clusters (dedicated clusters assigned to tenant)
 */
export function useAssignedManagedClusters(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.assigned(),
    queryFn: async () => {
      const response = await clusterApi.getAssignedManagedClusters()
      return response.data
    },
    enabled,
    staleTime: 10000, // 10 seconds — clusters change infrequently but must show up promptly
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch agents for a cluster
 */
export function useClusterAgents(clusterId: string, options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.agents(clusterId),
    queryFn: async () => {
      const response = await clusterApi.getAgents(clusterId)
      return response.data
    },
    enabled: enabled && !!clusterId,
    staleTime: 10000, // 10 seconds (agents update frequently)
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

/**
 * Hook to fetch events for a cluster
 */
export function useClusterEvents(
  clusterId: string,
  options?: { limit?: number; enabled?: boolean }
) {
  const { limit = 50, enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.events(clusterId),
    queryFn: async () => {
      const response = await clusterApi.getEvents(clusterId, limit)
      return response.data
    },
    enabled: enabled && !!clusterId,
    staleTime: 15000,
  })
}

/**
 * Hook to create a cluster
 */
export function useCreateCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateClusterRequest) => clusterApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
    },
  })
}

/**
 * Hook to update a cluster
 */
export function useUpdateCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClusterRequest }) =>
      clusterApi.update(id, data),
    onSuccess: (response, { id }) => {
      // #234: seed the detail cache with the saved cluster right away. Only
      // invalidating left a window where forms still read the pre-save
      // cluster, so a following save-on-toggle re-sent stale values (the
      // Ollama switch put an old model list back over a just-saved one).
      if (response?.data) queryClient.setQueryData(clusterKeys.detail(id), response.data)
      queryClient.invalidateQueries({ queryKey: clusterKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
    },
  })
}

/**
 * Hook to delete a cluster
 */
export function useDeleteCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    // A plain id, or { id, acknowledgeImpact } for the confirmed retry after a
    // 409 (deleteImpactFromError).
    mutationFn: (arg: string | { id: string; acknowledgeImpact?: boolean }) =>
      typeof arg === 'string'
        ? clusterApi.delete(arg)
        : clusterApi.delete(arg.id, { acknowledgeImpact: arg.acknowledgeImpact }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
    },
  })
}

/**
 * Hook to archive a BYOK cluster (reversible "Parked" state). Invalidates
 * the list (so it drops out of the default view), its own detail, and its
 * events (the archive action writes a local ClusterEvent, visible on the
 * cluster's own Events tab) -- mirrors useUpdateClusterStatus's three-key
 * invalidation shape.
 */
export function useArchiveCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => clusterApi.archive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clusterKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.events(id) })
    },
  })
}

/**
 * Hook to unarchive a BYOK cluster -- clears archived_at/archived_by and
 * restores default visibility. Same invalidation shape as useArchiveCluster.
 */
export function useUnarchiveCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => clusterApi.unarchive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clusterKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.events(id) })
    },
  })
}

/**
 * Hook to force-delete a cluster stuck in deleting state with offline agent
 */
export function useForceDeleteCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clusterId: string) => clusterApi.forceDelete(clusterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.all })
    },
  })
}

/**
 * H19 — this cluster's generic agent-settings descriptors + desired values
 * (`GET /tenant/clusters/{id}/agent-settings`). A2A is the first descriptor;
 * see AgentSettingDescriptor's doc comment — config over code, so a future
 * agent-directed setting needs no new query/hook, only a new descriptor.
 * Polled by the caller (ClusterDetail) via `refetchInterval` while any row is
 * non-converged — see deriveAgentSettingConvergence in AgentSettingsCard.tsx.
 */
export function useAgentSettings(
  clusterId: string,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  const { enabled = true, refetchInterval = false } = options || {}

  return useQuery({
    queryKey: clusterKeys.agentSettings(clusterId),
    queryFn: async () => {
      const response = await clusterApi.getAgentSettings(clusterId)
      return response.data
    },
    enabled: enabled && !!clusterId,
    staleTime: 10000,
    refetchInterval,
  })
}

/**
 * H19 — partial-merge patch of desired agent-settings values (`PUT
 * .../agent-settings`, `AgentSettingsUpdateRequest`). Invalidates the
 * agent-settings query (desired/updated_at/updated_by/changed_keys live
 * there) and the cluster's events — the backend audit-logs the change
 * (`audit.EventCatalogClusterAgentSettingsChanged`) whenever any key
 * actually changed.
 */
export function useUpdateAgentSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clusterId, patch }: { clusterId: string; patch: AgentSettingsUpdateRequest }) =>
      clusterApi.updateAgentSettings(clusterId, patch),
    onSuccess: (_, { clusterId }) => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.agentSettings(clusterId) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.events(clusterId) })
    },
  })
}

/**
 * Tenant default of the per-cluster airflow_task_log_archive setting, shown in
 * the Airflow task logs section of Infrastructure > Archival.
 */
export function useTaskLogArchiveDefault(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}
  return useQuery({
    queryKey: [...clusterKeys.all, 'task-log-archive-default'] as const,
    queryFn: async () => (await clusterApi.getTaskLogArchiveDefault()).data,
    enabled,
    staleTime: 10000,
  })
}

export function useSetTaskLogArchiveDefault() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (enabled: boolean) => clusterApi.setTaskLogArchiveDefault(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...clusterKeys.all, 'task-log-archive-default'] })
    },
  })
}

/** Copies the tenant default onto every cluster; refreshes every cluster's agent-settings. */
export function useApplyTaskLogArchiveDefault() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => clusterApi.applyTaskLogArchiveDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...clusterKeys.all, 'agent-settings'] })
    },
  })
}

/**
 * Hook to update cluster status
 */
export function useUpdateClusterStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string
      status: ClusterStatus
      reason?: string
    }) => clusterApi.updateStatus(id, status, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clusterKeys.events(id) })
    },
  })
}

/**
 * Hook to rotate agent token
 * Accepts an optional ttl_minutes (15–43200). Backend defaults to 1440 (24h).
 */
export function useRotateAgentToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clusterId, request }: { clusterId: string; request?: TokenRotateRequest }) =>
      clusterApi.rotateToken(clusterId, request),
    onSuccess: (_, { clusterId }) => {
      queryClient.invalidateQueries({ queryKey: clusterKeys.detail(clusterId) })
      queryClient.invalidateQueries({ queryKey: clusterKeys.events(clusterId) })
    },
  })
}

/**
 * Hook to fetch cluster health summary
 */
export function useClusterHealth(clusterId: string, options?: { enabled?: boolean }) {
  const { enabled = true } = options || {}

  return useQuery({
    queryKey: clusterKeys.health(clusterId),
    queryFn: async () => {
      const response = await clusterApi.getHealth(clusterId)
      return response.data
    },
    enabled: enabled && !!clusterId,
    staleTime: 10000, // 10 seconds
    refetchInterval: 15000, // Refetch every 15 seconds for live health
  })
}

/**
 * Hook to launch engine console (SSO) — supports langflow and airflow
 */
export function useConsoleLaunch() {
  return useMutation({
    mutationFn: ({ clusterId, engine }: { clusterId: string; engine?: 'langflow' | 'airflow' | 'dagster' }) =>
      clusterApi.consoleLaunch(clusterId, engine),
  })
}

/**
 * Helper to check if a cluster is operational
 */
export function isClusterOperational(cluster: Cluster): boolean {
  return cluster.status === 'active' || cluster.status === 'degraded'
}

/**
 * Helper to check if a cluster is in a terminal state
 */
export function isClusterTerminal(cluster: Cluster): boolean {
  return cluster.status === 'deleted' || cluster.status === 'failed'
}

/**
 * Helper to check if a cluster is BYOK
 */
export function isByokCluster(cluster: Cluster): boolean {
  return cluster.deployment_mode === 'byok'
}

/**
 * ConnectivityState — the top-line "is the agent reachable" axis.
 *
 * This is deliberately kept independent from WorkloadState (the per-engine
 * "deploying/ready/degraded" rollup on `ClusterWorkloadStatusResponse.data.engines[].health`,
 * see `features/managed-clusters/components/EngineRolloutPanel.tsx`). K8s
 * node-conditions pattern: two independent axes, never collapsed into one
 * banner — a cluster can honestly be "Agent: Connected" + "Engines: Deploying 1/4"
 * at the same time, and the UI must render that as two calm chips, not as
 * "not connected".
 */
export type ConnectivityState = 'connected' | 'disconnected' | 'unknown'

export interface ClusterConnectivity {
  state: ConnectivityState
  /** Which signal produced `state` — useful for debugging, not meant for UI copy. */
  source: 'health' | 'agent_status_fallback'
  /** cluster.status_reason, threaded through so badges can show it as subtext/tooltip. */
  reason?: string
}

/**
 * Derive the authoritative agent-connectivity signal for a BYOK cluster.
 *
 * Preferred source: `GET /tenant/clusters/{id}/health` (`online_agents` / `status`,
 * fetched via `useClusterHealth`). This is authoritative because it is a live,
 * server-computed aggregate — unlike the fields that historically got stitched
 * together (inconsistently) to answer "is the agent connected":
 *   - `Agent.status` stored enum — can go stale between heartbeats
 *   - `/workloads.agent_connected` — a secondary field on a separate, more
 *     slowly-polled resource-snapshot endpoint; it answers "was the last
 *     workload poll fresh", not "is the agent connected right now"
 * `cluster.status` / `cluster.status_reason` are also server-derived and are
 * threaded through as `reason` so any future contradiction between signals is
 * visible in the UI instead of silently picking whichever fragment is stale.
 *
 * Falls back to the `agents` list's stored status enum ONLY when `/health`
 * itself errors (not merely while it is still loading — callers should wait
 * for the query to settle before treating `healthError` as true).
 *
 * TODO(observability wave): a backend composite `last_contact` gauge (heartbeat
 * + transport probe fused server-side) is planned in another wave. Once shipped,
 * prefer that field directly instead of `online_agents > 0`.
 *
 * IMPORTANT: `total_agents === 0` (no agent has ever registered — normal for
 * `requested`/`provisioning`/`pending_agent`/`agent_connecting` clusters) MUST
 * map to `'unknown'`, never `'disconnected'`. The backend returns HTTP 200
 * with `online_agents: 0, total_agents: 0, status: 'unknown'` in that case —
 * treating a healthy 200 response with zero agents as "disconnected" would
 * reintroduce the exact "unknown reads as broken" bug this derivation fixes.
 */
export function deriveConnectivityState(
  cluster: Pick<Cluster, 'status_reason'>,
  health: ClusterHealth | undefined,
  healthError: boolean,
  agents: Agent[] | undefined,
): ClusterConnectivity {
  if (health) {
    if (health.total_agents === 0) {
      return { state: 'unknown', source: 'health', reason: cluster.status_reason }
    }
    return {
      state: health.online_agents > 0 ? 'connected' : 'disconnected',
      source: 'health',
      reason: cluster.status_reason,
    }
  }

  if (healthError) {
    const hasConnectedAgent = agents?.some((a) => a.status === 'connected') ?? false
    return {
      state: agents && agents.length > 0 ? (hasConnectedAgent ? 'connected' : 'disconnected') : 'unknown',
      source: 'agent_status_fallback',
      reason: cluster.status_reason,
    }
  }

  return { state: 'unknown', source: 'agent_status_fallback', reason: cluster.status_reason }
}

/**
 * Helper to check if a cluster is platform managed
 */
export function isPlatformManaged(cluster: Cluster): boolean {
  return (
    cluster.deployment_mode === 'platform_managed_shared' ||
    cluster.deployment_mode === 'platform_managed_dedicated'
  )
}

/**
 * Hook to fetch live Kubernetes workload status (pods + HPA) for BYOK clusters.
 * Mirrors useClusterWorkloadStatus from managed-clusters but uses the tenant API path.
 */
export function useByokClusterWorkloadStatus(
  id: string,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  const { enabled = true, refetchInterval = 5000 } = options || {}

  return useQuery({
    queryKey: clusterKeys.workloads(id),
    queryFn: async () => {
      const response = await clusterApi.getWorkloadStatus(id)
      return response.data
    },
    enabled: enabled && !!id,
    staleTime: 3000,
    refetchInterval,
    refetchIntervalInBackground: false,
  })
}

/**
 * Hook to fetch live connectivity status for a BYOK cluster.
 * Returns agent online state, redis_status, kafka_connectivity, and worker readiness
 * as reported by the connectivity-status API endpoint.
 */
export function useClusterConnectivityStatus(
  id: string,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  const { enabled = true, refetchInterval = 15000 } = options || {}

  return useQuery({
    queryKey: clusterKeys.connectivity(id),
    queryFn: () => clusterApi.getConnectivityStatus(id),
    enabled: enabled && !!id,
    staleTime: 10000,
    refetchInterval,
    refetchIntervalInBackground: false,
  })
}
